import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { mlmAPI, adminMlmAPI } from '../../services/api';
import { ChevronRight, ChevronDown, User, Move, Clock, Zap } from 'lucide-react';
import { getApiError } from '../../services/api';

const HOURS_72 = 72 * 60 * 60 * 1000;

function getTimeRemaining(createdAt) {
  if (!createdAt) return null;
  const elapsed = Date.now() - new Date(createdAt).getTime();
  const remaining = HOURS_72 - elapsed;
  if (remaining <= 0) return null;
  const hours = Math.floor(remaining / 3600000);
  const mins = Math.floor((remaining % 3600000) / 60000);
  return { hours, mins, total: remaining };
}

const MoveModal = ({ node, onClose, onMoved }) => {
  const { isDarkMode } = useTheme();
  const [positions, setPositions] = useState([]);
  const [selectedParent, setSelectedParent] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    adminMlmAPI.getAvailablePositions(1, 5)
      .then(res => setPositions(res.data))
      .catch(() => setError('Error loading positions'))
      .finally(() => setLoading(false));
  }, []);

  const handleMove = async () => {
    if (!selectedParent || !selectedPosition) return;
    setConfirming(true);
    try {
      await adminMlmAPI.repositionNode(node.id, {
        new_parent_id: selectedParent,
        new_position: selectedPosition,
      });
      onMoved();
      onClose();
    } catch (err) {
      setError(getApiError(err, 'Error moving node'));
    } finally {
      setConfirming(false);
    }
  };

  if (loading) return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 shadow-xl max-w-md w-full mx-4`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto" />
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 shadow-xl max-w-md w-full mx-4`} onClick={e => e.stopPropagation()}>
        <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
          Move: {node.name}
        </h3>
        <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Select a new parent position
        </p>

        {error && <p className="text-red-500 text-sm mb-3">{error}</p>}

        <div className="max-h-60 overflow-y-auto space-y-1 mb-4">
          {positions.filter(p => p.parent_id !== node.id).map((p, i) => (
            <button
              key={i}
              onClick={() => { setSelectedParent(p.parent_id); setSelectedPosition(p.position); }}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                selectedParent === p.parent_id && selectedPosition === p.position
                  ? 'bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300'
                  : `${isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`
              }`}
            >
              <span className="font-medium">{p.parent_name}</span>
              <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${p.position === 'left' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`}>
                {p.position}
              </span>
              <span className={`ml-2 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Lv {p.level}
              </span>
            </button>
          ))}
        </div>

        <div className="flex gap-2 justify-end">
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Cancel
          </button>
          <button
            onClick={handleMove}
            disabled={!selectedParent || !selectedPosition || confirming}
            className="px-4 py-2 rounded-lg text-sm bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {confirming ? 'Moving...' : 'Move'}
          </button>
        </div>
      </div>
    </div>
  );
};

const TreeNode = ({ node, level = 0, isDarkMode, userRole, onMove, onAutoPlace }) => {
  const [expanded, setExpanded] = useState(level < 2);

  if (!node) return null;

  const hasChildren = node.left || node.right;
  const positionColor = node.position === 'left' ? 'text-blue-500' : 'text-red-500';
  const timeLeft = getTimeRemaining(node.created_at);
  const canMove = (userRole === 'admin' || userRole === 'gestor') && timeLeft;

  return (
    <div className="select-none">
      <div
        className={`flex items-center gap-2 py-1.5 px-2 rounded-lg cursor-pointer transition-colors ${
          isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
        }`}
        style={{ paddingLeft: `${level * 24 + 8}px` }}
      >
        <span onClick={() => setExpanded(!expanded)} className="flex items-center">
          {hasChildren ? (
            expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />
          ) : (
            <div className="w-4" />
          )}
        </span>
        <User className={`w-4 h-4 ${positionColor}`} />
        <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
          {node.name || 'Empty'}
        </span>
        {node.sttora_id && (
          <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {node.sttora_id}
          </span>
        )}
        <span className={`text-xs font-medium ${positionColor}`}>
          [{node.position}]
        </span>
        <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          Lv {level}
        </span>
        {timeLeft && (
          <span className="flex items-center gap-1 text-xs text-amber-500">
            <Clock className="w-3 h-3" />
            {timeLeft.hours}h {timeLeft.mins}m
          </span>
        )}
        {canMove && (
          <button
            onClick={(e) => { e.stopPropagation(); onMove(node); }}
            className="ml-auto p-1 rounded hover:bg-indigo-100 dark:hover:bg-indigo-900 text-indigo-500"
            title="Move node"
          >
            <Move className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {expanded && hasChildren && (
        <div className="ml-2 border-l-2 border-dashed border-gray-300 dark:border-gray-600">
          <TreeNode node={node.left} level={level + 1} isDarkMode={isDarkMode} userRole={userRole} onMove={onMove} />
          <TreeNode node={node.right} level={level + 1} isDarkMode={isDarkMode} userRole={userRole} onMove={onMove} />
        </div>
      )}
    </div>
  );
};

const BinaryTree = () => {
  const { isDarkMode } = useTheme();
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [moveNode, setMoveNode] = useState(null);
  const [bestPos, setBestPos] = useState(null);
  const [bestPosLoading, setBestPosLoading] = useState(false);

  const loadTree = useCallback(() => {
    setLoading(true);
    mlmAPI.getBinaryTree()
      .then((res) => {
        setTree(res.data);
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        setUserRole(user.role);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadTree(); }, [loadTree]);

  const handleAutoPlace = async () => {
    setBestPosLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const res = await adminMlmAPI.getBestPosition(1, user.id);
      setBestPos(res.data);
    } catch (err) {
      setBestPos(null);
    } finally {
      setBestPosLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  const cardClass = `${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 shadow-sm border ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Binary Tree</h1>
          <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Your binary network structure</p>
        </div>
        {(userRole === 'admin' || userRole === 'gestor') && (
          <button
            onClick={handleAutoPlace}
            disabled={bestPosLoading}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm disabled:opacity-50"
          >
            <Zap className="w-4 h-4" />
            {bestPosLoading ? 'Calculating...' : 'Best Placement'}
          </button>
        )}
      </div>

      {bestPos && (
        <div className={`${cardClass} border-amber-400`}>
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-500" />
            <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
              Best available position: under <strong>{bestPos.parent_name || `position #${bestPos.parent_id}`}</strong> on the <span className={bestPos.position === 'left' ? 'text-blue-500' : 'text-red-500'}>{bestPos.position}</span> side
            </span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className={cardClass}>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Left Volume</p>
          <p className="text-2xl font-bold text-blue-500">
            {tree?.centers?.[0]?.left_volume || 0}
          </p>
        </div>
        <div className={cardClass}>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Right Volume</p>
          <p className="text-2xl font-bold text-red-500">
            {tree?.centers?.[0]?.right_volume || 0}
          </p>
        </div>
        <div className={cardClass}>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Team Size</p>
          <p className={`text-2xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
            {tree?.centers?.[0]?.team_size || 0}
          </p>
        </div>
      </div>

      <div className={cardClass}>
        <h2 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
          Tree Structure
          <span className="ml-2 text-sm font-normal text-gray-500">
            <span className="text-blue-500">Blue = Left</span> &middot; <span className="text-red-500">Red = Right</span>
            &middot; <span className="text-amber-500">Amber = Move window</span>
          </span>
        </h2>
        <div className={`border rounded-xl p-4 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          {tree?.tree ? (
            <TreeNode
              node={tree.tree}
              isDarkMode={isDarkMode}
              userRole={userRole}
              onMove={setMoveNode}
            />
          ) : (
            <p className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              No tree data available
            </p>
          )}
        </div>
      </div>

      {moveNode && <MoveModal node={moveNode} onClose={() => setMoveNode(null)} onMoved={loadTree} />}
    </motion.div>
  );
};

export default BinaryTree;
